import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@/components/Icon";
import api from "@/lib/api";
import { SheetBody } from "../ui/SheetHost";

/* SearchSheet (design 1d) — full-height search over games, teams and events.
   One field, scope chips (All / Casino / Sports). Real data: casino catalog
   (/casino/games) + sportsbook events (/events), fetched on open and filtered
   client-side. Uses the device keyboard for the field (the custom keypad is only
   for numeric stake entry, per pattern #04; text search keeps normal keyboard +
   autofill). */

const CHIPS = [["all", "All"], ["casino", "Casino"], ["sports", "Sports"]];

export default function SearchSheet({ close }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [scope, setScope] = useState("all");
  const [games, setGames] = useState([]);
  const [events, setEvents] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [g, e] = await Promise.allSettled([api.get("/casino/games"), api.get("/events")]);
        if (!alive) return;
        if (g.status === "fulfilled") setGames(normalizeGames(g.value.data));
        if (e.status === "fulfilled") setEvents(normalizeEvents(e.value.data));
      } catch { /* leave empty; UI shows no-results */ }
    })();
    return () => { alive = false; };
  }, []);

  const query = q.trim().toLowerCase();
  const gameHits = useMemo(
    () => (scope === "sports" || !query ? [] : games.filter((g) => g.name.toLowerCase().includes(query)).slice(0, 12)),
    [games, query, scope]
  );
  const eventHits = useMemo(
    () => (scope === "casino" || !query ? [] : events.filter((e) => e.label.toLowerCase().includes(query)).slice(0, 12)),
    [events, query, scope]
  );
  const hasResults = gameHits.length || eventHits.length;

  const openGame = (g) => { close(); navigate(`/casino?game=${encodeURIComponent(g.slug || g.id)}`); };
  const openEvent = (e) => { close(); navigate(`/event/${e.id}`); };

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "4px 16px 10px" }}>
        <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8, height: 44, borderRadius: 14, background: "#0E0E11", boxShadow: "inset 0 0 0 1px #26262B,inset 0 1px 2px rgba(0,0,0,.45)", padding: "0 12px" }}>
          <Icon name="search" size={18} style={{ color: "#71717a" }} />
          <input
            ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Games, teams and players" inputMode="search" aria-label="Search"
            style={{ flex: 1, background: "transparent", border: 0, outline: "none", color: "#fff", font: "600 16px 'Outfit'" }}
          />
          {q && <button type="button" aria-label="Clear" onClick={() => setQ("")} style={{ background: "none", border: 0, cursor: "pointer", padding: 0 }}><Icon name="x" size={16} style={{ color: "#71717a" }} /></button>}
        </div>
        <button type="button" onClick={close} style={{ background: "none", border: 0, cursor: "pointer", font: "600 15px 'Outfit'", color: "#00D632" }}>Cancel</button>
      </div>

      <div style={{ display: "flex", gap: 8, padding: "0 16px 8px" }}>
        {CHIPS.map(([id, label]) => {
          const on = scope === id;
          return (
            <button key={id} type="button" onClick={() => setScope(id)} aria-pressed={on}
              style={{ height: 32, padding: "0 14px", borderRadius: 999, border: 0, cursor: "pointer", font: `${on ? 700 : 600} 13px 'Outfit'`,
                background: on ? "#F4F4F5" : "#18181C", color: on ? "#0A0A0A" : "#D4D4D8", boxShadow: on ? "none" : "inset 0 0 0 1px #26262B" }}>
              {label}
            </button>
          );
        })}
      </div>

      <SheetBody>
        {!query && <p style={{ font: "500 13px 'Outfit'", color: "#71717a", padding: "8px 0" }}>Search for a game, team or player.</p>}
        {query && !hasResults && <p style={{ font: "500 13px 'Outfit'", color: "#71717a", padding: "8px 0" }}>No matches for “{q}”.</p>}

        {gameHits.length > 0 && <div style={sectionLabel}>GAMES</div>}
        {gameHits.map((g) => (
          <button key={`g-${g.id}`} type="button" onClick={() => openGame(g)} style={rowStyle}>
            <span style={{ width: 40, height: 40, flex: "none", borderRadius: 10, overflow: "hidden", background: "#141417" }}>
              {g.img && <img src={g.img} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
            </span>
            <span style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
              <span style={{ display: "block", font: "700 15px 'Outfit'", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{g.name}</span>
              <span style={{ font: "500 12px 'Outfit'", color: "#a1a1aa" }}>{g.sub || "Casino"}</span>
            </span>
            <Icon name="chevron-right" size={16} style={{ color: "#52525b" }} />
          </button>
        ))}

        {eventHits.length > 0 && <div style={sectionLabel}>SPORTS</div>}
        {eventHits.map((e) => (
          <button key={`e-${e.id}`} type="button" onClick={() => openEvent(e)} style={rowStyle}>
            <span style={{ width: 40, height: 40, flex: "none", borderRadius: 10, background: "#141417", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Icon name="sportsbook" size={18} style={{ color: "#a1a1aa" }} />
            </span>
            <span style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
              <span style={{ display: "block", font: "700 15px 'Outfit'", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{e.label}</span>
              <span style={{ font: "500 12px 'Outfit'", color: "#a1a1aa" }}>{e.sub || "Event"}</span>
            </span>
            <Icon name="chevron-right" size={16} style={{ color: "#52525b" }} />
          </button>
        ))}
      </SheetBody>
    </>
  );
}

const sectionLabel = { font: "700 11px 'Outfit'", letterSpacing: ".16em", color: "#71717a", margin: "12px 0 6px" };
const rowStyle = { width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "8px 0", background: "none", border: 0, cursor: "pointer" };

function normalizeGames(data) {
  const arr = Array.isArray(data) ? data : data?.games || data?.items || [];
  return arr.map((g) => ({
    id: g.id || g.slug || g.key || g.name,
    slug: g.slug || g.key || g.id,
    name: g.name || g.title || g.label || "Game",
    sub: g.provider_label || g.provider || (g.is_original ? "PRYMEBET Original" : undefined),
    img: g.thumbnail_url || g.img || g.image,
  })).filter((g) => g.name);
}
function normalizeEvents(data) {
  const arr = Array.isArray(data) ? data : data?.events || data?.items || [];
  return arr.map((e) => ({
    id: e.id || e._id,
    label: e.label || e.name || [e.home, e.away].filter(Boolean).join(" @ ") || [e.home_team, e.away_team].filter(Boolean).join(" @ "),
    sub: e.league || e.sport,
  })).filter((e) => e.id && e.label);
}
